import { message } from "antd";
import React, { useEffect, useState } from "react";
import "./style.css";
import Lottie from "lottie-react";
import reportGeneration from "../../../Assets/Animations/Report Generation.json";
import { getMore } from "../../../API/moreAnalysis";
import { useDateFilter } from "../../../Contexts/DateFilterContext";
import LineChart from "../../../Components/Charts/LineChart";
import AreaChart from "../../../Components/Charts/AreaChart";
import GenericTable from "../../../Components/GenericTable";

const MoreAnalysis = () => {
  const defaultMoreData = {
    top_performing_vehicles: {
      data: [],
      labels: [],
      average_idle: 0,
      average_mileage: 0,
      average_fuel: 0,
    },
    worst_performing_vehicles: {
      labels: [],
      mileage: [],
      idle: [],
      fuel: [],
      average_idle: 0,
      average_mileage: 0,
      average_fuel: 0,
    },
    all_vehicles: [],
    fuel_efficiency: { fleet: [], vehicle: [], labels: [] },
    fuel_efficiency_average: 0,
    corelation: { data: [], labels: [] },
    corr_fuel_usage_average: 0,
    corr_idle_hours_average: 0,
    corr_distance_travelled_average: 0,
    corr_movement_hours_average: 0,
  };
  const [moreData, setMoreData] = useState(defaultMoreData);
  const [loading, setLoading] = useState(true);
  const { from, to } = useDateFilter();

  useEffect(() => {
    if (!to || !from) {
      return;
    }

    const fetchMore = async () => {
      try {
        setLoading(true);
        const response = await getMore(to, from);
        setMoreData({
          ...defaultMoreData,
          ...response.data,
          top_performing_vehicles: {
            ...defaultMoreData.top_performing_vehicles,
            ...(response.data?.top_performing_vehicles || {}),
            data: Array.isArray(response.data?.top_performing_vehicles?.data)
              ? response.data.top_performing_vehicles.data
              : [],
            labels: Array.isArray(
              response.data?.top_performing_vehicles?.labels
            )
              ? response.data.top_performing_vehicles.labels
              : [],
          },
          worst_performing_vehicles: {
            ...defaultMoreData.worst_performing_vehicles,
            ...(response.data?.worst_performing_vehicles || {}),
            labels: Array.isArray(
              response.data?.worst_performing_vehicles?.labels
            )
              ? response.data.worst_performing_vehicles.labels
              : [],
            mileage: Array.isArray(
              response.data?.worst_performing_vehicles?.mileage
            )
              ? response.data.worst_performing_vehicles.mileage
              : [],
            idle: Array.isArray(response.data?.worst_performing_vehicles?.idle)
              ? response.data.worst_performing_vehicles.idle
              : [],
            fuel: Array.isArray(response.data?.worst_performing_vehicles?.fuel)
              ? response.data.worst_performing_vehicles.fuel
              : [],
          },
          fuel_efficiency: {
            ...defaultMoreData.fuel_efficiency,
            ...(response.data?.fuel_efficiency || {}),
            fleet: Array.isArray(response.data?.fuel_efficiency?.fleet)
              ? response.data.fuel_efficiency.fleet
              : [],
            vehicle: Array.isArray(response.data?.fuel_efficiency?.vehicle)
              ? response.data.fuel_efficiency.vehicle
              : [],
            labels: Array.isArray(response.data?.fuel_efficiency?.labels)
              ? response.data.fuel_efficiency.labels
              : [],
          },
          corelation: {
            ...defaultMoreData.corelation,
            ...(response.data?.corelation || {}),
            data: Array.isArray(response.data?.corelation?.data)
              ? response.data.corelation.data
              : [],
            labels: Array.isArray(response.data?.corelation?.labels)
              ? response.data.corelation.labels
              : [],
          },
        });
        setLoading(false);
      } catch (error) {
        message.error("Failed to fetch fuel data");
        console.log(error, "Failed to fetch fuel data");
        setLoading(false);
      }
    };

    fetchMore();
  }, [to, from]);

  const columns = [
    {
      title: <span className="vehicle-title">Vehicle Name</span>,
      dataIndex: "vehicle_name",
      key: "vehicle_name",
      sorter: (a, b) =>
        a.vehicle_name.localeCompare(b.vehicle_name, undefined, {
          numeric: true,
        }),
    },
    {
      title: "Total Travelled",
      dataIndex: "total_travelled",
      key: "total_travelled",
      sorter: (a, b) =>
        a.total_travelled.localeCompare(b.total_travelled, undefined, {
          numeric: true,
        }),
    },
    {
      title: "Fuel Consumed",
      dataIndex: "fuel_consumed",
      key: "fuel_consumed",
      sorter: (a, b) => a.fuel_consumed - b.fuel_consumed,
      render: (value) => `${value} L`,
    },
    {
      title: "Mileage",
      dataIndex: "mileage",
      key: "mileage",
      sorter: (a, b) =>
        a.mileage.localeCompare(b.mileage, undefined, {
          numeric: true,
        }),
    },
    {
      title: "Fuel Economy",
      dataIndex: "fuel_economy",
      key: "fuel_economy",
      sorter: (a, b) => a.fuel_economy - b.fuel_economy,
      render: (value) => `${value} KM/L`,
    },
  ];

  return (
    <div className="more-analysis">
      {loading ? (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          className="loader-container"
        >
          <Lottie
            animationData={reportGeneration}
            loop={true}
            style={{ width: 150, height: 150 }}
          />
        </div>
      ) : (
        <div className="rows-container">
          <div className="row tabs">
            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Fleet Top Performing</span>
                <div className="value-legend-grid">
                  <div className="value-legend-col">
                    <span
                      className="value-box value-box1"
                      style={{ color: "#1FC16B" }}
                    >
                      {moreData?.top_performing_vehicles?.average_fuel} L
                    </span>
                    <span className="legend-item">
                      <span
                        className="legend-color"
                        style={{ background: "#1FC16B" }}
                      ></span>
                      Fuel Con.
                    </span>
                  </div>

                  <div className="value-legend-col">
                    <span
                      className="value-box value-box2"
                      style={{ color: "#FB3748" }}
                    >
                      {moreData?.top_performing_vehicles?.average_mileage} Km/L
                    </span>
                    <span className="legend-item">
                      <span
                        className="legend-color"
                        style={{ background: "#FB3748" }}
                      ></span>
                      Mileage
                    </span>
                  </div>

                  <div className="value-legend-col">
                    <span
                      className="value-box value-box3"
                      style={{ color: "#155DFC" }}
                    >
                      {moreData?.top_performing_vehicles?.average_idle} Hr
                    </span>
                    <span className="legend-item">
                      <span
                        className="legend-color"
                        style={{ background: "#155DFC" }}
                      ></span>
                      Idle
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Fleet Worst Performing</span>
                <div className="value-legend-grid">
                  <div className="value-legend-col">
                    <span
                      className="value-box value-box1"
                      style={{ color: "#1FC16B" }}
                    >
                      {moreData?.worst_performing_vehicles?.average_fuel} L
                    </span>
                    <span className="legend-item">
                      <span
                        className="legend-color"
                        style={{ background: "#1FC16B" }}
                      ></span>
                      Fuel Con.
                    </span>
                  </div>

                  <div className="value-legend-col">
                    <span
                      className="value-box value-box2"
                      style={{ color: "#FB3748" }}
                    >
                      {moreData?.worst_performing_vehicles?.average_mileage}{" "}
                      Km/L
                    </span>
                    <span className="legend-item">
                      <span
                        className="legend-color"
                        style={{ background: "#FB3748" }}
                      ></span>
                      Mileage
                    </span>
                  </div>

                  <div className="value-legend-col">
                    <span
                      className="value-box value-box3"
                      style={{ color: "#155DFC" }}
                    >
                      {moreData?.worst_performing_vehicles?.average_idle} Hr
                    </span>
                    <span className="legend-item">
                      <span
                        className="legend-color"
                        style={{ background: "#155DFC" }}
                      ></span>
                      Idle
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Fleet Fuel Efficiency Avg</span>
                <span className="value-box" style={{ color: "#1FC16B" }}>
                  {moreData?.fuel_efficiency_average} km/L
                </span>
              </div>
            </div>

            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Fleet Correlation</span>
                <div className="values-row">
                  <div className="value-legend-col">
                    <span
                      className="value-box value-box3"
                      style={{ color: "#155DFC" }}
                    >
                      {moreData?.corr_fuel_usage_average} L
                    </span>
                    <span className="legend-item">
                      <span
                        className="legend-color"
                        style={{ background: "#155DFC" }}
                      ></span>
                      Avg Fuel Con.
                    </span>
                  </div>
                  <div className="value-legend-col">
                    <span
                      className="value-box value-box1"
                      style={{ color: "#1FC16B" }}
                    >
                      {moreData?.corr_idle_hours_average} Hr
                    </span>
                    <span className="legend-item">
                      <span
                        className="legend-color"
                        style={{ background: "#1FC16B" }}
                      ></span>
                      Avg Idle Hrs
                    </span>
                  </div>
                  <div className="value-legend-col">
                    <span
                      className="value-box value-box2"
                      style={{ color: "#FB3748" }}
                    >
                      {moreData?.corr_distance_travelled_average} Km
                    </span>
                    <span className="legend-item">
                      <span
                        className="legend-color"
                        style={{ background: "#FB3748" }}
                      ></span>
                      Avg Mileage
                    </span>
                  </div>
                  <div className="value-legend-col">
                    <span
                      className="value-box value-box4"
                      style={{ color: "#F59E0B" }}
                    >
                      {moreData?.corr_movement_hours_average} Hr
                    </span>
                    <span className="legend-item">
                      <span
                        className="legend-color"
                        style={{ background: "#F59E0B" }}
                      ></span>
                      Avg Mov. Hrs
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="graph">
              <LineChart
                graphData={{
                  labels: moreData?.top_performing_vehicles.labels,
                  idle: moreData?.top_performing_vehicles.idle,
                  mileage: moreData?.top_performing_vehicles.mileage,
                  fuel: moreData?.top_performing_vehicles.fuel,
                }}
                lengthKeys={3}
                colourTheme={["#FB3748", "#1FC16B", "#2662D9"]}
                graphTitle="Top Performing Vehicles"
                selectTenByDefault={true}
                units={["Hr", "Km", "L"]}
                classNameCSS="top-performing-vehicles"
              />
            </div>
          </div>

          <div className="row">
            <div className="graph">
              <LineChart
                graphData={{
                  labels: moreData?.worst_performing_vehicles.labels,
                  idle: moreData?.worst_performing_vehicles.idle,
                  mileage: moreData?.worst_performing_vehicles.mileage,
                  fuel: moreData?.worst_performing_vehicles.fuel,
                }}
                lengthKeys={3}
                colourTheme={["#FB3748", "#1FC16B", "#2662D9"]}
                graphTitle="Worst Performing Vehicles"
                selectTenByDefault={true}
                units={["Hr", "Km", "L"]}
                classNameCSS="worst-performing-vehicles"
              />
            </div>
          </div>
          <div className="row">
            <div className="graph">
              <AreaChart
                graphData={{
                  labels: moreData?.fuel_efficiency.labels,
                  fuel: moreData?.fuel_efficiency.fuel,
                }}
                lengthKeys={1}
                colourTheme={["#1FC16B"]}
                graphTitle="Fuel Efficiency"
                selectTenByDefault={true}
                units={["Km/L"]}
                classNameCSS="efficiency"
              />
            </div>
          </div>
          <GenericTable
            title="All Vehicles"
            dataSource={moreData?.all_vehicles}
            columns={columns}
          />

          <div className="row">
            <div className="graph">
              <LineChart
                graphData={{
                  labels: moreData?.corelation.labels,
                  fuel_usage: moreData?.corelation.fuel_usage,
                  idle_hours: moreData.corelation.idle_hours,
                  distance_travelled: moreData?.corelation.distance_travelled,
                  movement_hours: moreData?.corelation.movement_hours,
                }}
                lengthKeys={4}
                colourTheme={["#FB3748", "#1FC16B", "#2662D9", "#DFB400"]}
                graphTitle="Correlation"
                selectTenByDefault={true}
                units={["L", "Hr", "Km", "Hr"]}
                classNameCSS="correlation"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MoreAnalysis;
